/**
 * usePersistedValue — one owner for an inline-edited field on the tend card.
 *
 * Grew out of usePersistedText (the category pill, Buggie Boo 3ec9a8a4…fc56)
 * when the copy text reverted on production (Buggie Boo 3f89a8a4…3521,
 * 2026-10-13): a card that had NOT received the edit — the same post in the
 * strip under the item modal, another tab, a teammate's browser — compared
 * its local text against the refreshed prop, decided it had an edit, and
 * wrote its stale text back on every 90 s strip poll.
 *
 * The contract, for any value (text, switch, list, map):
 *  - The value is STATE; the control is its projection. A remount re-seeds
 *    from `getValue()`, never from the item prop.
 *  - Only an `edit()` from THIS instance ever saves. A server value arriving
 *    through the prop is adopted (and reported through `onExternalChange`
 *    so the caller re-seeds a contentEditable), never written back.
 *  - Save-as-you-go on a debounce, an immediate save on `flush` (blur) and a
 *    last save on unmount while an edit is pending.
 *  - The compare is against the last value WE saved, not the prop: typing
 *    back to the original after a save is still a change.
 *  - A server value equal to one our saves already replaced is a stale
 *    refetch (a strip fetch that was in flight when we saved) and is ignored
 *    for STALE_REFETCH_WINDOW_MS; after that it can only mean someone wrote
 *    it back, and it is adopted.
 *  - A pending edit is never clobbered by a server change.
 *  - A different `recordKey` resets the field to the new record's value,
 *    after flushing any pending edit to the old record.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type Save<T> = (value: T) => Promise<unknown>;

export interface PersistedValueOptions<T> {
  /** The server's current value for the field (the prop). */
  serverValue: T;
  /** Identity of the record the field belongs to. */
  recordKey: string | number;
  /** Writes the value to the server. */
  save: Save<T>;
  /** A genuine external change was adopted; project it (e.g. into a contentEditable). */
  onExternalChange?: (value: T) => void;
  /** Save-as-you-go delay in ms. */
  delay?: number;
  /**
   * Identity used for equality and the stale-refetch guard. Strings compare
   * as themselves; lists in order; plain objects by sorted keys.
   */
  key?: (value: T) => string;
  /**
   * Something that changes on every re-read from the server (the item
   * object itself works). Lets a re-read carrying the SAME value as the last
   * one re-run the adoption check, so a value ignored as a stale refetch is
   * adopted once the window has passed and it is still what the server holds.
   */
  serverVersion?: unknown;
}

export interface PersistedValue<T> {
  /** The field's value (render from this). */
  value: T;
  /** The latest value without a render (for a ref callback seeding a new node). */
  getValue: () => T;
  /** From the control: the user's new value. */
  edit: (next: T) => void;
  /** Save now if an edit is pending (blur). */
  flush: () => void;
}

/** How long a re-read carrying a value our save replaced counts as stale. */
export const STALE_REFETCH_WINDOW_MS = 15_000;

export function defaultValueKey(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[k] = (value as Record<string, unknown>)[k];
    }
    return JSON.stringify(sorted);
  }
  return JSON.stringify(value) ?? 'undefined';
}

export function usePersistedValue<T>({
  serverValue,
  recordKey,
  save,
  onExternalChange,
  delay = 500,
  key = defaultValueKey,
  serverVersion,
}: PersistedValueOptions<T>): PersistedValue<T> {
  const [value, setValue] = useState<T>(serverValue);
  const valueRef = useRef<T>(serverValue);
  // What the server holds, as far as we know (the prop at mount, then our saves).
  const lastSavedRef = useRef<T>(serverValue);
  // Keys of server values our saves replaced, with when: a re-read carrying one
  // inside the window is stale.
  const supersededRef = useRef<Map<string, number>>(new Map());
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef<Save<T>>(save);
  saveRef.current = save;
  const onExternalChangeRef = useRef(onExternalChange);
  onExternalChangeRef.current = onExternalChange;
  const keyRef = useRef(key);
  keyRef.current = key;
  const recordKeyRef = useRef(recordKey);
  // The save bound to the record being edited. Updated AFTER the sync effect
  // each render, so a record change can still flush to the old record.
  const recordSaveRef = useRef<Save<T>>(save);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const commit = useCallback((next: T, saveFn: Save<T>) => {
    dirtyRef.current = false;
    clearTimer();
    const previous = lastSavedRef.current;
    if (keyRef.current(next) === keyRef.current(previous)) return;
    const record = recordKeyRef.current;
    supersededRef.current.set(keyRef.current(previous), Date.now());
    lastSavedRef.current = next;
    saveFn(next).catch((err: unknown) => {
      console.error('Failed to save field:', err);
      // The server still holds `previous`; the next blur/unmount retries.
      if (recordKeyRef.current === record && lastSavedRef.current === next) {
        lastSavedRef.current = previous;
        dirtyRef.current = true;
      }
    });
  }, [clearTimer]);

  const flush = useCallback(() => {
    if (!dirtyRef.current) return;
    commit(valueRef.current, saveRef.current);
  }, [commit]);

  const edit = useCallback((next: T) => {
    valueRef.current = next;
    dirtyRef.current = true;
    setValue(next);
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flush();
    }, delay);
  }, [clearTimer, delay, flush]);

  const getValue = useCallback(() => valueRef.current, []);

  // Server value / record changes.
  useEffect(() => {
    if (recordKeyRef.current !== recordKey) {
      if (dirtyRef.current) commit(valueRef.current, recordSaveRef.current);
      recordKeyRef.current = recordKey;
      supersededRef.current = new Map();
      lastSavedRef.current = serverValue;
      valueRef.current = serverValue;
      dirtyRef.current = false;
      setValue(serverValue);
      onExternalChangeRef.current?.(serverValue);
      return;
    }
    const incoming = keyRef.current(serverValue);
    if (incoming === keyRef.current(lastSavedRef.current)) return;
    if (dirtyRef.current) return;                        // our pending edit is newer
    const replacedAt = supersededRef.current.get(incoming);
    if (replacedAt !== undefined) {
      if (Date.now() - replacedAt < STALE_REFETCH_WINDOW_MS) return;  // a stale refetch
      supersededRef.current.delete(incoming);           // old enough: someone wrote it back
    }
    lastSavedRef.current = serverValue;
    valueRef.current = serverValue;
    setValue(serverValue);
    onExternalChangeRef.current?.(serverValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValue, serverVersion, recordKey, commit]);

  useEffect(() => {
    recordSaveRef.current = save;
  });

  // Unmount: a pending edit is saved on the way out.
  useEffect(() => () => { flush(); }, [flush]);

  return { value, getValue, edit, flush };
}
